import api from '@/api/index';
import { debugError } from '@/static/develop.js';
import { TypeCall, Nullable } from '@/types/common';
import { PaymentType } from '@/types/components';
import {
  IRequestCreateContractV2,
  IRequestCreateMcsSubuser,
  IRequestPartner,
  IRequestPartnerLogin,
  IRequestPartnerLoginHeader,
  IRequestPartnerLoginHeaderApp,
  IRequestSignupOAuth,
  IRequestSubmitSurvey,
  RequestScripts,
  RequestScriptDownload,
  RequestScriptRealTime,
  RequestScriptState,
  RequestScriptStateDetail,
  RequestScriptTitle,
  RequestScriptTranslation,
  RequestAllocationSoma,
  IRequestCreateMcsSubuserAuthHeader,
  IRequestCreateMcsSubuserAuth,
  IRequestDeleteMcsSubuser,
  RequestScript,
} from '@/types/request';
import {
  IMicLabelResponse,
  IResponseAuthorizationKey,
  IResponseCallManage,
  IResponseMcsSubUser,
  IResponseMicGainSetting,
  IResponsePartnerHome,
  IResponseUserCountry,
  IResponseUserInfo,
  IResponseUserMicDevice,
  ResponseMCSSubUser,
  ResponseScript,
  ResponseScriptState,
  ResponseScriptTranslation,
} from '@/types/response';
import { SOCIAL_PROVIDER_MAPPER } from '@/utils/constants/common';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';

// 클라이언트 사이드
export const requestAuthKey = async () => {
  return await api.get<IResponseAuthorizationKey>('/api/partner/auth/keys');
};

// 로그인 요청
export const requestLogin = async (headers: IRequestPartnerLoginHeader, body: IRequestPartnerLogin) => {
  return await api.post('/api/partner/auth/login', body, { headers });
};

// 로그인 요청
export const requestLoginApp = async (
  headers: IRequestPartnerLoginHeader | IRequestPartnerLoginHeaderApp,
  body: IRequestPartnerLogin
) => {
  return await api.post('/api/partner/auth/login/app', body, { headers });
};

export const fetchWebTokenResponse = (userId) => api.get(`/api/test/cookie/${userId}`);

export const fetchUserInfo = () => api.get<IResponseUserInfo>('/api/user/info/web');

export const fetchRegisteredDeviceId = (base64UrlSafedDeviceLabel) =>
  api.get<IResponseUserMicDevice>(
    `/api/chat/business/call/voice/device/registered?deviceLabel=${base64UrlSafedDeviceLabel}`
  );

export const fetchAllRegisteredDevices = () => api.get(`/api/chat/business/call/voice/device/registered/all`);

export const fetchAnyDeviceRegistered = () => api.get(`/api/chat/business/call/voice/registered/any`);

export const fetchChatList = (searchWord, offset, onlyMine, selectedTags) => {
  const params = {
    offset,
    searchWord,
    onlyMine,
  };

  if (Array.isArray(selectedTags) && selectedTags.length > 0) {
    params['selectedTags'] = selectedTags.join(',');
  }

  return api.get('/api/chat/web', { params });
};

export const fetchChatInfo = (chatId) => api.get(`/api/chat/${chatId}/info`);

export const fetchChatLogs = (chatId, offset) =>
  api.get(`/api/chat/business/call/${chatId}/log`, {
    params: { offset: offset },
  });

export const renameChatAPI = (chatId, newName) => api.post(`/api/chat/${chatId}/name`, { chatName: newName });

export const callConsultation = (code, chatId, langCode) =>
  api.post(`/api/chat/business/call/${code}/${chatId}/consultation`, {
    languageCode: langCode,
  });

export const checkCallConsultation = (code, chatId, langCode) =>
  api.post(`/api/chat/business/call/${code}/${chatId}/consultation/valid`);

export const fetchAuthCheck = (loginId: string) => api.get('/api/partner/auth', { params: { loginId } });

// 파트너 어드민 사이드
export const fetchPublicKey = () => api.get('/api/partner/auth/keys');

export const fetchVoiceUrl = (chatId, key) => api.get(`/api/chat/business/call/voice/${chatId}/${key}`);

export const loginAdmin = async (userId, encryptedPassword, type) =>
  api.post(
    `/api/partner/auth/login`,
    { type },
    {
      headers: {
        userId: userId,
        password: encryptedPassword,
      },
    }
  );

export const fetchCallLogHistoryFiles = async (callType: TypeCall, params: { code: string | null }) =>
  api.get(`/api/chat/business/call/${callType}/history`, { params });

export const downloadCallLogHistory = async (id, filename) =>
  api
    .get(`/api/chat/business/call/history/${id}`, { responseType: 'blob' })
    .then((response) => {
      // Content-Disposition 헤더 추출
      const fileName = `${filename}.txt`;

      // Blob URL 생성 및 다운로드 링크 생성
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      // 링크 및 URL 정리
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error('파일 다운로드 실패:', error);
    });

export const requestAdminHome = () => {
  return api.get<IResponsePartnerHome>('/api/partner/home');
};

export const fetchAdminHomeEmployeeAccessInfo = () => api.get('/api/partner/home/employee/access');

export const fetchDashboardUsageWeek = () => api.get('/api/partner/dashboard/week');

export const fetchDashboardUsage = (prepayment) => api.get(`/api/partner/dashboard/usage?prepayment=${prepayment}`);

export const fetchPartnerInfo = () => api.get('/api/partner');

// QR코드관리 API
export const fetchQrCodeList = () => api.get('/api/partner/qrcode');

export const generateQrCode = (data) =>
  api.post('/api/partner/qrcode', {
    titles: data.titles,
    partnerGroupId: data.partnerGroupId,
    survey: data.survey,
  });

export const deleteQrCode = (code) => api.post(`/api/partner/qrcode/${code}/delete`, code);

// 사내관리 API
export const updateCompanyInfo = (formData: IRequestPartner) =>
  api.post('/api/partner', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const fetchStatsUsageFee = (year, month) =>
  api.get('/api/partner/stats/call/usage', { params: { year, month } });

export const fetchStatsCall = (year) => api.get('/api/partner/stats/call', { params: { year } });

export const fetchYearMonths = () => api.get('/api/partner/stats/call/detail/yearmonth');

export const fetchStatsContract = () => api.get('/api/partner/stats/call/contract');

export const fetchStatsCallDetailLanguage = (year, month) =>
  api.get('/api/partner/stats/call/detail/language', {
    params: {
      year: year,
      month: month,
    },
  });

export const fetchStatsCallDetailCall = (offset, searchWord) =>
  api.get('/api/partner/stats/call/detail/call', {
    params: {
      offset: offset,
      searchWord: searchWord,
    },
  });

export const fetchStatsCallDetailCallV2 = (offset: string | null, searchWord: string, selectedTags: string) => {
  return api.get('/api/v2/partner/stats/call/detail/call', {
    params: {
      offset,
      ...(searchWord ? { searchWord } : {}),
      ...(selectedTags ? { selectedTags } : {}),
    },
  });
};

export const fetchSelfServiceCreditDetail = (offset: string | null = null, type: string | null = null) => {
  return api.get('/api/v1/selfservice/credit/history', {
    params: {
      offset,
      type,
    },
  });
};

export const fetchEmployeeList = (
  offset?: Nullable<string>,
  searchWord?: Nullable<string>,
  onlySubUser: boolean = false
) =>
  api.get('/api/partner/employee', {
    params: {
      offset: offset,
      searchWord: searchWord,
      onlySubUser,
    },
  });

export const createEmployee = ({ nickname, username, languageCode, groupIds, userId, password }) =>
  api.post(
    '/api/partner/employee',
    {
      nickname,
      username,
      languageCode,
      groupIds,
    },
    {
      headers: {
        userId,
        password,
      },
    }
  );

export const updateEmployee = (userId, encryptedPassword, formData) => {
  return api.post('/api/partner/employee/update', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      userId: userId,
      password: encryptedPassword,
    },
  });
};

export const createUserTeam = (teamId: string) => {
  return api.post('/api/v1/selfservice/user/team', { teamId });
};

export const updatePassword = (password) =>
  api.post('/api/partner/employee/update/password', null, {
    headers: { password },
  });

export const deleteEmployee = (userId) =>
  api.post(`/api/partner/employee/delete`, null, {
    headers: {
      userId,
    },
  });

// 사내관리 API : 파트너 그룹
export const fetchGroupList = (offset, searchWord, all) =>
  api.get('/api/partner/group', {
    params: { offset, searchWord, all },
  });
export const createGroup = (inputName) => api.post('/api/partner/group', { groupName: inputName });

//파트너 그룹에 호스트 추가,제거
export const manageGroupEmployee = (partnerGroupId, userIds) =>
  api.post(`/api/partner/group/${partnerGroupId}/employee`, {
    userIds: userIds,
  }); //body userIds
export const deleteGroup = (partnerGroupId) => api.post(`/api/partner/group/${partnerGroupId}/delete`);

// 통역 관련 API
export const holdConsultationCall = (chatId) => api.post(`/api/partner/consultation/${chatId}/hold`);
export const fetchWaitingCallList = () => api.get('/api/partner/consultation/wait');

// memo api
export const fetchRecentMemo = () => api.get('/api/partner/home/memo/recent');
export const updateBaseUrlToLocal = () => (api.defaults.baseURL = 'http://localhost:8080');

//NEW
export const deleteCall = (code) => api.post(`/api/partner/call/${code}/delete`);

export const nextChatId = (code) => api.get(`/api/chat/business/call/${code}/next`, { responseType: 'text' });

export const deleteVoice = (deviceId) => api.post(`/api/chat/business/call/voice/delete/${deviceId}`);

export const evict = (userId) => api.post(`/api/chat/business/call/evict/${userId}`);

export const getMultiCalls = (offset, searchWord, selectedTags, isFavorite?: boolean) => {
  const params = {
    offset,
    ...(searchWord && { searchWord }),
    ...(isFavorite && { isFavorite }),
  };

  if (Array.isArray(selectedTags) && selectedTags.length > 0) {
    params['selectedTags'] = selectedTags.join(',');
  }

  return api.get('/api/v2/partner/call', { params });
};

export const getMultiCallsDashboard = (offset, searchWord, selectedTags, isFavorite) => {
  const params = {
    offset,
    searchWord,
  };

  if (Array.isArray(selectedTags) && selectedTags.length > 0) {
    params['selectedTags'] = selectedTags.join(',');
  }

  if (isFavorite !== undefined) {
    params['isFavorite'] = isFavorite;
  }

  return api.get(`/api/v2/partner/call`, { params });
};

export const createConsultationCall = (formData) =>
  api.post(`/api/partner/consultation`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const createFairCon = (formData) =>
  api.post(`/api/partner/general`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const updateConsultation = (formData, code) =>
  api.post(`/api/partner/consultation/${code}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const updateFairCon = (formData, code) =>
  api.post(`/api/partner/general/${code}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const createAudienceCall = (formData) =>
  api.post(`/api/partner/audience`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const updateAudienceCall = (formData, code) =>
  api.post(`/api/partner/audience/${code}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const getAttendees = (chatId) => api.get(`/api/chat/${chatId}/attendee`);

export const leaveChat = (chatId) => api.post(`/api/chat/${chatId}/delete`);

export const amICalling = () => api.get(`/api/chat/business/call/calling`);

export const evictMyself = () => api.post(`/api/chat/business/call/evict/myself`);

export const fetchQrCode = (code) => {
  return api.get(`/api/qr/${code}`);
};

export const inferenceHealthCheck = (zone, port) =>
  api.get(`/api/health/inference`, {
    params: {
      zone: zone,
      port: port,
    },
  });

export const callFaircon = (code, chatId, nickname, langCode) =>
  api.post(`/api/chat/business/call/${code}/${chatId}/faircon`, {
    nickname: stringToBase64UrlSafe(nickname),
    languageCode: langCode,
  });

export const logout = async () => await api.post(`/api/partner/auth/logout`);

export const registerVoice = async (recordedAudio, deviceLabel, lang, reference) => {
  const formData = new FormData();
  formData.append('voice', recordedAudio.wavBlob);

  try {
    const result = await api.post('/api/chat/business/call/voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }, //header param
      params: {
        sampleRate: recordedAudio.sampleRate,
        deviceLabel,
        lang,
        reference,
      },
    });
    if (result.success) {
      return result.data;
    } else if (result.errorCode === 1021 || result.errorCode === 1030 || result.errorCode === 1031) {
      console.log(`result.errorCode: ${result.errorCode}`);
      return result.errorCode;
    }
  } catch (error) {
    if (error.errorCode === 1021 || error.errorCode === 1030 || error.errorCode === 1031) {
      debugError('Error cuz of too short status:', error);
      return error.errorCode;
    } else {
      // 요청 설정 중에 발생한 에러
      debugError('Error:', error.message);
    }
    return null;
  }
};

// Dist
export const fetchCustomerList = (offset, searchWord) =>
  api.get('/api/dist/customer', {
    params: {
      offset: offset,
      searchWord: searchWord,
    },
  });
export const fetchCustomerAll = () => api.get('/api/dist/customer/all');
export const addCustomer = (partnerName, businessNumber, managerName, managerContract, adminId, partnerCode) =>
  api.post('/api/dist/customer', {
    partnerName,
    businessNumber,
    managerName,
    managerContract,
    adminId,
    partnerCode,
  });
export const updateCustomer = (partnerId, partnerName, businessNumber, managerName, managerContract, partnerCode) =>
  api.post(`/api/dist/customer/${partnerId}`, {
    partnerName,
    businessNumber,
    managerName,
    managerContract,
    partnerCode,
  });
export const deleteCustomer = (partnerId) => api.post(`/api/dist/customer/${partnerId}/delete`);

export const fetchContractAll = () => api.get('/api/contract/customer/all');

export const fetchContractWithUsage = (partnerId) => api.get(`/api/contract/customer?partnerId=${partnerId}`);

export const fetchContractStats = (partnerId) => api.get(`/api/dist/constomer/stats/${partnerId}`);

export const createContract = (
  partnerId,
  planId,
  prepayment,
  basicFee,
  prepaymentUnitPrice,
  postpaidUnitPrice,
  endCallWhenExceeded,
  startDate,
  endDate,
  prepaymentMinutes,
  isDemo
) =>
  api.post(`/api/contract/customer/${partnerId}`, {
    planId,
    prepayment,
    basicFee,
    prepaymentUnitPrice,
    postpaidUnitPrice,
    endCallWhenExceeded,
    startDate,
    endDate,
    prepaymentMinutes,
    isDemo,
  });

export const deleteContract = (partnerId, contractId) =>
  api.post(`/api/contract/customer/${partnerId}/${contractId}/delete`);

export const pauseContract = (partnerId, contractId) =>
  api.post(`/api/contract/customer/${partnerId}/${contractId}/pause`);

export const restartContract = (partnerId, contractId) =>
  api.post(`/api/contract/customer/${partnerId}/${contractId}/restart`);

export const fetchDashboardTotalUsage = (prepayment) => api.get(`/api/dist/dashboard/usage?prepayment=${prepayment}`);

export const fetchDashboardExceed = () => api.get(`/api/dist/dashboard/exceed`);

export const fetchDashboardContract = () => api.get(`/api/dist/dashboard/contract`);

export const fetchMessagesPastConference = (chatId: string, languageCode: string, offset) =>
  api.get(`api/chat/logs/conference/${chatId}`, {
    params: { languageCode, offset },
  });

export const fetchMessagePastConference = (chatId: string, callLogId: string, param) =>
  api.get(`api/chat/logs/conference/${chatId}/${callLogId}`, param);

export const fetchMessagesPastDm = (chatId, offset = null) =>
  api.get(`api/chat/logs/dm/${chatId}`, {
    params: { offset },
  });

export const fetchLanguageV1 = (domain: string = '') => api.get(`/api/v1/language${domain ? '/' + domain : ''}`);

export const addFavorite = (type, code) => api.post(`api/favorite/add?type=${type}&targetKey=${code}`);

export const deleteFavorite = (type, code) => api.post(`api/favorite/remove?type=${type}&targetKey=${code}`);

export const createChatRoom = (formData) =>
  api.post<IResponseCallManage>('/api/admin/qr', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const editChatRoom = (code: string, formData) =>
  api.post(`/api/admin/qr/${code}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const signupOAuth = (paramsSignup: IRequestSignupOAuth, isMobile = false) => {
  const {
    oAuth,
    userId,
    accessToken,
    storeAgreement,
    marketingAgreement,
    countryCode,
    languageCode,
    referralCode,
    email,
  } = paramsSignup;

  const registerType = SOCIAL_PROVIDER_MAPPER[oAuth];
  if (!registerType) throw new Error(`Unknown OAuth provider: ${oAuth}`);

  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  const isApple = oAuth === 'apple';
  const isGoogle = oAuth === 'google';
  const isGoogleWeb = isGoogle && !isMobile;
  const isAppleMobile = isMobile && isApple;

  const basePath = isGoogleWeb ? '/api/v2/auth/3rd' : '/api/auth/3rd';
  const suffix = isMobile ? '/app' : '';
  const nicknameQuery = isAppleMobile ? `?nickname=${paramsSignup.nickname}` : '';

  const url = `${basePath}/signup/${registerType}${suffix}${nicknameQuery}`;

  const uriOrigin = window.location.origin;

  return api.post(url, null, {
    headers: {
      'Content-Type': 'application/json',
      userId: userId,
      Authorization: accessToken,
    },
    params: {
      redirectUri: `${uriOrigin}/callback.html`,
      storeAgreement,
      marketingAgreement,
      countryCode,
      languageCode,
      email,
      ...(referralCode && { referralCode }),
    },
  });
};

export const fetchInfoPartner = (partnerCode: string) => api.get(`api/partner/${partnerCode}/info`);

export const createContractV2 = (partnerId: number, params: IRequestCreateContractV2) =>
  api.post(`/api/v2/contract/customer/${partnerId}`, params);

export const fetchContractV2 = (partnerId: number) => api.get(`/api/v2/contract/customer`, { params: { partnerId } });

export const pauseContractV2 = (partnerId, contractId: number) =>
  api.post(`/api/v2/contract/customer/${partnerId}/${contractId}/pause`);

export const restartContractV2 = (partnerId, contractId: number) =>
  api.post(`/api/v2/contract/customer/${partnerId}/${contractId}/restart`);

export const deleteContractV2 = (partnerId, contractId: number) =>
  api.post(`/api/v2/contract/customer/${partnerId}/${contractId}/delete`);

export const fetchContractPricingV2 = (contractId: number) =>
  api.get('/api/v2/contract/customer/pricing', { params: { contractId } });

export const fetchSelfServiceContractPricingV2 = () => api.get('/api/v2/contract/customer/pricing/selfservice');

export const subscriptionChange = (subscriptionDetails) =>
  api.post('/api/v1/selfservice/subscription/change', subscriptionDetails);

export const subscriptionChangeRenew = (id, params) =>
  api.post(`/api/v1/selfservice/subscription/${id}/change/additional`, params);

export const fetchUserSubscription = () => {
  return api.get('/api/v1/selfservice/subscription');
};

export const beginSubscription = (params) => api.post('/api/v1/selfservice/subscription', params);

export const subscriptionCancel = () => api.post('/api/v1/selfservice/subscription/cancel');

export const addSelfServiceInquiry = (params) => api.post('/api/selfservice/inquiry', params);

export const addSelfServiceInquiryFollowup = (params) =>
  api.post(`/api/selfservice/inquiry/${params.inquiryId}`, params);

export const fetchSelfServiceInquiry = (params) => api.get('/api/selfservice/inquiry', params);

export const fetchSelfServiceInquiryResponse = (inquiryId) => api.get(`/api/selfservice/inquiry/${inquiryId}`);

export const fetchSelfServiceAccessToken = () => api.get('/api/v1/selfservice/payment/auth');

export const getSelfServicePaymentMethod = (paymentType) => {
  return api.get('/api/v1/selfservice/payment/method', {
    params: { paymentType },
  });
};

export const deleteSelfServicePaymentMethod = (paymentType) =>
  api.post(`/api/v1/selfservice/payment/method/delete?paymentType=${paymentType}`);

export const fetchSelfServiceCreditCurrent = () => api.get('/api/v1/selfservice/credit/summary');

export const fetchSelfServicePaymentHistory = (params) => {
  return api.get('/api/v1/selfservice/credit/history', { params: params });
};

export const deleteSelfServiceUser = (userId: string) =>
  api.post('/api/v1/selfservice/credit/history', {}, { headers: { userId } });

export const fetchAgreementTerms = (paymentType = 'TOSS') =>
  api.get('/api/v1/selfservice/terms', { params: { paymentType } });

export const cancelSubscriptionReservation = () => api.post('/api/v1/selfservice/subscription/cancel/reservation');

export const setConferenceNotStarted = (code) => api.get(`api/v1/conference/${code}/NOT_STARTED`);

export const setConferenceInProgress = (code) => api.get(`api/v1/conference/${code}/IN_PROGRESS`);

export const setConferenceEnded = (code) => api.get(`api/v1/conference/${code}/ENDED`);

export const fetchMcsSubUser = (code: string) => api.get<IResponseMcsSubUser[]>(`/api/qr/${code}/person`);

export const createMcsSubUser = (code: string, body: IRequestCreateMcsSubuser) => {
  return api.post<ResponseMCSSubUser>(`/api/v1/mcs/mic/subUser/${code}`, body);
};

export const createMcsSubUserToken = (
  params: IRequestCreateMcsSubuserAuth,
  headers: IRequestCreateMcsSubuserAuthHeader
) => {
  return api.post('/api/partner/auth/subUser', null, { params, headers });
};

export const deleteMcsSubUser = (code: string, headers: IRequestDeleteMcsSubuser) => {
  return api.post(`/api/v1/mcs/mic/subUser/${code}/remove`, null, { headers });
};

export const fetchMicLabelList = () => api.get<IMicLabelResponse[]>('/api/v1/mcs/mic/label');

export const updateMicLabelList = (body: IMicLabelResponse[]) => api.post('/api/v1/mcs/mic/label', body);

export const createSetupIntent = () => api.post('/api/v1/selfservice/payment/setup-intent');

export const confirmSetup = (params) => api.post('/api/v1/selfservice/payment/confirm-setup', params);

export const setCardDefault = (paymentType) =>
  api.post(`/api/v1/selfservice/payment/method/existing?paymentType=${paymentType}`);

export const addCardNew = (paymentType: PaymentType) =>
  api.post(`/api/v1/selfservice/payment/method/new?paymentType=${paymentType}`);

export const loginSelfService = (company: string, id, credential) => {
  return api.post(
    `/api/auth/3rd/login/${company}`,
    {
      userId: id,
      registerType: company,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        userId: id,
        Authorization: credential,
      },
    }
  );
};

export const loginSelfServiceGoogle = (company: string, code: string) => {
  const urlOrigin = window.location.origin;
  return api.post(
    `/api/v2/auth/3rd/login/${company}?redirectUri=${urlOrigin}/callback.html`,
    {
      registerType: company,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: code,
      },
    }
  );
};

export const loginSocialMobile = ({
  provider,
  id,
  credential,
}: {
  provider: string;
  id: string;
  credential: string;
}) => {
  return api.post(
    `/api/auth/3rd/login/${provider}/app`,
    {
      userId: id,
      registerType: provider,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        userId: id,
        Authorization: credential,
      },
    }
  );
};

export const loginSocialMobileRefresh = ({ provider, refreshToken }) => {
  return api.post(
    `/api/auth/3rd/login/${provider}/app?refresh=true`,
    {
      registerType: provider,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: refreshToken,
      },
    }
  );
};

export const deleteUserSelfService = (userId: string) =>
  api.post(
    '/api/v1/selfservice/user/delete',
    {},
    {
      headers: {
        userId,
      },
    }
  );

export const getPaymentHistory = (status, offset) => {
  let url = `/api/v1/selfservice/payment/history?offset=${offset}`;
  if (status) url += `&status=${status}`;
  return api.get(url);
};

export const fetchUserCountry = async () => {
  return api.get<IResponseUserCountry>('/api/user/country') || {};
};

export const getPlanDetail = (plan?: string) => {
  return api.get('/api/v1/selfservice/subscription/plan/detail', {
    params: plan ? { plan } : undefined,
  });
};

export const fetchSubuserNickname = (nickname: string, headers: { Userid: string }) => {
  return api.post('/api/partner/auth/subUser', null, {
    headers,
    params: { nickname },
  });
};

export const getZoomRegister = (code) => {
  return api.get(`/api/zoom?code=${code}`);
};

export const submitSurveyAnswer = (params: IRequestSubmitSurvey) => {
  return api.post('/api/v1/selfservice/survey', params);
};

export const fetchMicGainSetting = (): IResponseMicGainSetting => {
  return api.get<IResponseMicGainSetting>('/api/setting/mic/gain');
};

export const fetchLanguageOptions = () => {
  return api.get('/api/chat/business/call/language/option');
};

export const fetchWebsocketUrl = (params: RequestAllocationSoma) => {
  return api.get<string>('/api/server/allocation/soma', { params });
};

export const fetchPlanCreditPerPrice = () => {
  return api.get('/api/v1/selfservice/payment/now/info');
};

export const chargeCredit = (credit) => {
  return api.post('/api/v1/selfservice/payment/now', { credit });
};

export const fetchScript = (chatId: string, params: RequestScript) => {
  return api.get<ChatScript>(`/api/v1/script/${chatId}`, { params });
};

export const fetchScripts = (params: RequestScripts) => {
  return api.get<ResponseScript>('/api/v1/script', { params });
};

export const fetchScriptState = (params: RequestScriptState) => {
  return api.get<ResponseScriptState[]>(`/api/v1/script/state`, { params });
};

export const fetchScriptStateDetail = (chatId: string, params: RequestScriptStateDetail) => {
  return api.get<ResponseScriptState>(`/api/v1/script/${chatId}/state`, { params });
};

export const fetchScriptDownload = (chatId: string, params: RequestScriptDownload) => {
  return api.get(`/api/v1/script/${chatId}/download`, { params });
};

export const fetchScriptDownloadRealTime = (chatId: string, params: RequestScriptRealTime) => {
  return api.get(`/api/v1/script/${chatId}/download/real-time`, { params, responseType: 'blob' });
};

export const updateScriptTitle = (chatId: string, body: RequestScriptTitle) => {
  return api.post(`/api/v1/script/${chatId}/title`, body);
};

export const updateScriptTranslation = (chatId: string, params: RequestScriptTranslation) => {
  return api.post<ResponseScriptTranslation>(`/api/v1/script/${chatId}/translation`, null, { params });
};

export default api;
